import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { fetchDataWithAutoRetry } from '../../utils/fetchDataWithAutoRetry'
import { kucoinApiEndpoint } from '../constants'
import { errorResponseSchema } from '../schema'
import { createPrivateGetHeaders } from '../utils'
import { type Deposit, depositResponseSchema } from './schema'

const urlPath = '/api/v1/deposits'

type FetchDepositHistoriesParameters = {
  apiKey: string
  apiSecret: string
  password: string
  from: number
  to: number
  limit: number
  proxy?: string
  headers?: Record<string, string>
}

/** {@link https://www.kucoin.com/docs/rest/funding/deposit/get-deposit-list}
 * - Request via this endpoint to get deposit list Items are paginated and sorted to show the latest first. See the Pagination section for retrieving additional entries after the first page.
 */
export const fetchDepositHistories = ({
  apiKey,
  apiSecret,
  password,
  from,
  to,
  limit,
  proxy,
  headers,
}: FetchDepositHistoriesParameters): Promise<readonly Deposit[]> =>
  fetchDataWithAutoRetry({
    from,
    to,
    limit,
    desc: true,
    fetchData: async ({ from, to, limit, desc }) => {
      if (!desc) {
        throw new Error('desc must be true')
      }
      if (limit > 500) {
        throw new Error('limit must be less than or equal to 500')
      }

      const url = new URL(urlPath, kucoinApiEndpoint)
      url.searchParams.append('pageSize', limit.toString())
      url.searchParams.append('startAt', from.toString())
      url.searchParams.append('endAt', to.toString())
      url.searchParams.append('status', 'SUCCESS')
      const query = url.searchParams.toString()

      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: {
              ...headers,
              ...(await createPrivateGetHeaders({
                apiKey,
                apiSecret,
                passphrase: password,
                urlPath,
                query: query.length > 0 ? query : '',
              })),
            },
          },
          schema: z.union([depositResponseSchema, errorResponseSchema]),
        },
      )
      if ('msg' in response) {
        throw new Error(response.msg)
      }
      return response.data.items
    },
  })
